/**
 * Split diff renderer — shows original and new text in stacked blocks.
 *
 * Original block highlights removed words in red.
 * New block highlights added words in green.
 * Unchanged text is plain in both blocks.
 */

import React, { useMemo } from 'react';

interface DiffSegment {
  type: 'equal' | 'insert' | 'delete';
  text: string;
}

/**
 * Simple word-level diff algorithm (no external dependency).
 * Uses longest common subsequence on word arrays.
 */
function computeWordDiff(oldText: string, newText: string): DiffSegment[] {
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);

  const m = oldWords.length;
  const n = newWords.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const result: DiffSegment[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      result.push({ type: 'equal', text: oldWords[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ type: 'insert', text: newWords[j - 1] });
      j--;
    } else {
      result.push({ type: 'delete', text: oldWords[i - 1] });
      i--;
    }
  }

  result.reverse();

  // Merge consecutive segments of the same type
  const segments: DiffSegment[] = [];
  for (const seg of result) {
    if (segments.length > 0 && segments[segments.length - 1].type === seg.type) {
      segments[segments.length - 1].text += seg.text;
    } else {
      segments.push({ ...seg });
    }
  }

  return segments;
}

interface Props {
  original: string;
  modified: string;
}

const InlineDiff: React.FC<Props> = ({ original, modified }) => {
  const segments = useMemo(
    () => computeWordDiff(original, modified),
    [original, modified],
  );

  // Original text: show equal + delete segments (skip inserts)
  const originalParts = segments.filter((s) => s.type !== 'insert');
  // New text: show equal + insert segments (skip deletes)
  const newParts = segments.filter((s) => s.type !== 'delete');

  const hasDeletes = segments.some((s) => s.type === 'delete');
  const hasInserts = segments.some((s) => s.type === 'insert');

  return (
    <div className="space-y-2">
      {/* Original with removals highlighted */}
      {hasDeletes && (
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70 mb-1 block">
            Original
          </span>
          <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground">
            {originalParts.map((seg, idx) =>
              seg.type === 'delete' ? (
                <span
                  key={idx}
                  className="bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 line-through decoration-red-400/60"
                >
                  {seg.text}
                </span>
              ) : (
                <span key={idx}>{seg.text}</span>
              ),
            )}
          </div>
        </div>
      )}

      {/* New text with additions highlighted */}
      {hasInserts && (
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70 mb-1 block">
            Updated
          </span>
          <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-body">
            {newParts.map((seg, idx) =>
              seg.type === 'insert' ? (
                <span
                  key={idx}
                  className="bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400"
                >
                  {seg.text}
                </span>
              ) : (
                <span key={idx}>{seg.text}</span>
              ),
            )}
          </div>
        </div>
      )}

      {/* If only equal segments (no actual diff), just show the text */}
      {!hasDeletes && !hasInserts && (
        <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-body">
          {original}
        </div>
      )}
    </div>
  );
};

export default InlineDiff;
